import { logout } from '@/store/slices/authSlice'
import { useAppDispatch, useAuth } from '@/store/hooks'
import { MdLogout } from 'react-icons/md'
import { Navigate, Outlet, useNavigate } from 'react-router-dom'
import { endServerSession } from '@/lib/api/session-refresh'

/**
 * Portal do cliente: o paciente acompanha os próprios atendimentos e paga os
 * procedimentos em modo flex.
 *
 * Reusa a sessão normal do sistema (userType CLIENT, mesma conta do app) — não
 * há token próprio como no portal do parceiro. O gate é o `userType`: um
 * operador logado que digitar /client é mandado de volta ao painel, e não
 * autenticado vai para o login do portal.
 */
export default function ClientPortalLayout() {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { isAuthenticated, user } = useAuth()

    if (!isAuthenticated) {
        return <Navigate to="/client" replace />
    }
    if (user?.userType !== 'CLIENT') {
        return <Navigate to="/" replace />
    }

    const handleLogout = async () => {
        await endServerSession()
        dispatch(logout())
        navigate('/client', { replace: true })
    }

    const firstName = (user?.name || '').split(' ')[0]

    return (
        <div
            className="min-h-screen bg-gradient-to-b from-secondary/[0.06] to-base-200/40"
            data-theme="atendimento"
        >
            {/* Header flutuante, arredondado — cara de app, distinto do painel. */}
            <header className="sticky top-0 z-30 backdrop-blur-md bg-base-100/80 border-b border-base-200/70">
                <div className="max-w-4xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-base-100 elevation-1 flex items-center justify-center">
                            <img
                                src="/logo512.png"
                                alt="Mediar Saúde"
                                className="w-6 h-6 object-contain"
                            />
                        </div>
                        <div className="leading-none">
                            <p className="text-[15px] font-bold text-base-content tracking-tight">
                                Mediar Saúde
                            </p>
                            <p className="text-[10px] font-semibold text-base-content/40 uppercase tracking-wide">
                                Área do cliente
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {firstName && (
                            <span className="hidden sm:inline text-sm text-base-content/60">
                                Olá,{' '}
                                <span className="font-semibold text-base-content">
                                    {firstName}
                                </span>
                            </span>
                        )}
                        <button
                            type="button"
                            className="w-9 h-9 rounded-xl flex items-center justify-center text-base-content/60 hover:bg-base-200 hover:text-error transition-colors"
                            onClick={handleLogout}
                            title="Sair"
                            aria-label="Sair"
                        >
                            <MdLogout className="text-lg" />
                        </button>
                    </div>
                </div>
            </header>

            <main className="px-4 lg:px-6 py-6 max-w-4xl mx-auto">
                <Outlet />
            </main>
        </div>
    )
}
